import React, { useState, useEffect, type ReactNode, useCallback } from "react";
import { SchedulingOverviewWidget } from "../components/scheduling-overview-widget";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Switch } from "@/components/ui/switch";

import { ClientCombobox } from "@/components/ui/client-combobox";

import { EmployeeCombobox } from "@/components/ui/employee-combobox";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import {
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  Pencil,
  SlidersHorizontal,
  AlertTriangle,
  Lock,
  MessageSquarePlus,
  Send,
  Clock3,
  Loader2,
} from "lucide-react";
import { SensitiveData } from "@/components/sensitive-data";

import {
  PROJECT_ROLE_DISPLAY_ORDER,
  type ProjectRoleType,
  type ProjectCustomFieldDefinition,
  type ProjectRoleAssignment,
  type ProjectNote,
} from "@/types";

import { type SchedulingSummary } from "@/lib/mock-scheduling-data";
import { fetchSchedulingSummary } from "@/services/schedulingService";

import {
  formatCurrency,
  formatPercent,
  formatDate,
  formatDateForInput,
  cn,
} from "@/lib/utils";

import { getUtilisationSettings, computeUtilisationRate } from '@/lib/utilisation-settings-storage';

import { useProjectDetailsContext } from "../context";
import { usePermissions } from '@/contexts/PermissionsContext';

export function OverviewTab() {
  const ctx = useProjectDetailsContext();
  const { canPerformAction, isTabEditable } = usePermissions();
  const canEditOverview = canPerformAction('edit') && isTabEditable('overview');
  const [liveSummary, setLiveSummary] = useState<SchedulingSummary | null>(null);
  // Destructure all needed values from context
  const {
    project, client, clients, assignableEmployees, projectManager, accountManager,
    navigate, setActiveTab,
    enrichedBudgetData,

    schedulerConnected,
    invoicedNetTotal,
    effectiveCompletionPercent, autoCalculatedCompletionPercent,
    completionPercentEditing,
    completionPercentInputValue, setCompletionPercentInputValue,
    handleCompletionPercentEditStart, handleCompletionPercentEditConfirm,
    handleCompletionPercentEditCancel, handleCompletionModeChange,
    completionAuditEntries,
    setAuditCategoryFilter,
    isProjectInfoEditMode,
    editFormData,
    recentClientIds,
    isClientChangeLocked,
    isKeyPeopleEditMode,
    keyPeopleEditState,
    hasKeyPeopleUnsavedChanges,
    getRoleLabel, isRoleEnabled, isRoleRequired,
    recentProjectManagerIds, recentAccountManagerIds,
    contentFieldSettings, customFieldDefs,
    noteLog, newNoteValue, setNewNoteValue, isSubmittingNote,
    isClientInfoEditMode,
    clientInfoEditData,

    isRevRecLocked, wipInvoiceTier, isTypeLocked,
    endDateBeforeStart,
    startDateMovedPastTimesheets,
    endDateShortenedPastAllocations,
    isSaveBlocked,
    handleClientChange,
    formatProjectType, formatRevenueRecognition,

    openClientPanel,
    // Additional items that may be used
    isObjectivesEditMode, objectivesEditValue, setObjectivesEditValue,
    isDeliverablesEditMode, deliverablesEditValue, setDeliverablesEditValue,
    handleAddNote,
    handleSaveObjectives, handleSaveDeliverables,
    isSavingObjectives, isSavingDeliverables,
    handleEnterKeyPeopleEditMode, handleSaveKeyPeople, handleCancelKeyPeopleEdit, isSavingKeyPeople, handleProjectManagerChange, handleAccountManagerChange,
    handleEnterUnifiedEditMode, handleCancelUnifiedEdit, handleSaveUnifiedChanges, hasUnifiedUnsavedChanges, isSavingUnified,
    isAttributesEditMode,
    attributesEditState, setAttributesEditState,
    attributesValidationErrors, setAttributesValidationErrors,
    handleEnterAttributesEditMode, handleCancelAttributesEdit, handleSaveAttributes, isSavingAttributes,
    handleEnterObjectivesEdit, handleCancelObjectivesEdit,
    handleEnterDeliverablesEdit, handleCancelDeliverablesEdit,
    handleFieldChange, handleKeyPeopleRoleChange,
    timeEntries, billingInvoices,
  } = ctx;

  // Fetch live scheduling summary for the overview widget
  const projectIdStr = project?.id != null ? String(project.id) : null;
  useEffect(() => {
    if (!projectIdStr || !schedulerConnected) return;
    let cancelled = false;

    fetchSchedulingSummary(projectIdStr).then((resp) => {
      if (cancelled) return;
      setLiveSummary({
        totalBudgetHours: resp.totalBudgetHours,
        totalScheduledHours: resp.scheduledHours,
        coveragePercent: resp.bookingCoveragePercent,
        totalBookings: resp.totalBookings,
        confirmedBookings: resp.confirmedBookings,
        plannedBookings: resp.plannedBookings,
        unmetRoles: (resp.unmetRoles ?? []).map((r) => ({
          roleId: r.roleId,
          roleName: r.roleName,
          budgetHours: r.budgetHours,
          scheduledHours: r.scheduledHours,
          gapHours: r.gapHours,
        })),
        bookingsEndDate: resp.bookingsEndDate ?? '',
        projectEndDate: resp.projectEndDate ?? '',
        healthStatus: (resp.healthStatus as SchedulingSummary['healthStatus']) ?? 'NoData',
        warnings: [],
      });
    }).catch((err) => {
      if (!cancelled) {
        console.error('[OverviewTab] Error fetching scheduling summary:', err);
      }
    });

    return () => { cancelled = true; };
  }, [projectIdStr, schedulerConnected]);

  if (!project) return null;

  // ── Billable cascade dialog state ────────────────────────────────────────
  const [billableCascadeOpen, setBillableCascadeOpen] = useState(false);

  // Determine whether the project has existing financial activity so the dialog
  // can surface a contextual warning to the user before they confirm.
  const hasExistingInvoices = billingInvoices.length > 0;
  const hasExistingTimeEntries = timeEntries.length > 0;
  const hasExistingActivity = hasExistingInvoices || hasExistingTimeEntries;

  const handleBillableToggle = useCallback((checked: boolean) => {
    if (!checked) {
      // Toggling OFF — show cascade dialog before committing
      setBillableCascadeOpen(true);
    } else {
      // Toggling ON — no cascade needed, just update project
      handleFieldChange('billable', true);
    }
  }, [handleFieldChange]);

  const handleBillableCascadeConfirm = useCallback((updateCostCentres: boolean) => {
    setBillableCascadeOpen(false);
    handleFieldChange('billable', false);
    if (updateCostCentres) {
      // Cascade to all cost centres — handled via handleFieldChange with a special key
      // that the context / save handler interprets as a bulk cost centre update.
      handleFieldChange('cascadeBillableToCostCentres', true);
    }
  }, [handleFieldChange]);

  const handleBillableCascadeCancel = useCallback(() => {
    // User dismissed — revert toggle (do nothing, field stays unchanged)
    setBillableCascadeOpen(false);
  }, []);

  return (
    <>
                {/* Key Metrics - Always Visible */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Key Metrics</h2>
                  </div>
                  <div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
                      {/* Sales Variance card */}
                      {(() => {
                        const actualRevenue = enrichedBudgetData?.salesBudget?.totals?.actual ?? project.actualSales ?? project.salesActual ?? project.actualRevenue ?? 0;
                        const budgetRevenue = enrichedBudgetData?.summary?.revenue?.budget ?? project.budgetRevenue ?? 0;
                        // Same server-aligned "Invoiced" total the Billing tab summary bar
                        // shows (all raised invoices except Draft / Void, net of credit
                        // notes) — net of VAT so it reconciles against the net Budget Sales.
                        const invoiced = invoicedNetTotal;
                        const remainingToInvoice = budgetRevenue - invoiced;
                        const isAboveBudget = actualRevenue >= budgetRevenue;
                        return (
                          <Card>
                            <CardHeader className="mt-2 px-4 pb-1">
                              <CardTitle className="text-sm font-medium">Sales Variance</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pt-2 pb-3">
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className="text-2xl font-bold">
                                  <SensitiveData>{formatCurrency(actualRevenue)}</SensitiveData>
                                </div>
                                {isAboveBudget ? (
                                  <TrendingUp className="h-4 w-4 text-success-500" />
                                ) : (
                                  <TrendingDown className="h-4 w-4 text-error-500" />
                                )}
                              </div>
                              <button
                                onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=S`)}
                                className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5 text-left"
                              >
                                Actual Sales
                              </button>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5">
                                <div className="flex justify-between text-xs">
                                  <button
                                    onClick={() => setActiveTab('sales-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >Budget Sales</button>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(budgetRevenue)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Invoiced</span>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(invoiced)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between text-xs border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {remainingToInvoice >= 0 ? "Remaining to Invoice" : "Over Billed"}
                                  </span>
                                  <SensitiveData>
                                    <span className={`font-medium ${remainingToInvoice >= 0 ? "text-success-600" : "text-error-600"}`}>
                                      {formatCurrency(Math.abs(remainingToInvoice))}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {/* Cost Variance card */}
                      {(() => {
                        const actualCosts = enrichedBudgetData?.costBudget?.totals?.actual ?? project.actualCosts ?? 0;
                        const budgetCosts = enrichedBudgetData?.costBudget?.totals?.budget ?? ((project.budgetLabour ?? 0) + (project.budgetExpense ?? 0));
                        const costVariance = enrichedBudgetData ? (budgetCosts - actualCosts) : (project.costVariance ?? (budgetCosts - actualCosts));
                        const costVariancePct = budgetCosts !== 0 ? ((actualCosts - budgetCosts) / budgetCosts) * 100 : 0;
                        const isOverBudget = actualCosts > budgetCosts;
                        return (
                          <Card>
                            <CardHeader className="mt-2 px-4 pb-1">
                              <CardTitle className="text-sm font-medium">Cost Variance</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pt-2 pb-3">
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className="text-2xl font-bold">
                                  <SensitiveData>{formatCurrency(actualCosts)}</SensitiveData>
                                </div>
                                {isOverBudget ? (
                                  <TrendingUp className="h-4 w-4 text-error-500" />
                                ) : (
                                  <TrendingDown className="h-4 w-4 text-success-500" />
                                )}
                              </div>
                              <button
                                onClick={() => navigate(`/project-transactions?projectCode=${project.code}&entryType=C`)}
                                className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity mt-0.5"
                              >Actual Costs</button>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => setActiveTab('cost-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >Budget Costs</button>
                                  <SensitiveData>
                                    <span className="font-medium">{formatCurrency(budgetCosts)}</span>
                                  </SensitiveData>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Variance (%)</span>
                                  <span className={`font-medium ${costVariancePct > 0 ? 'text-error-600' : costVariancePct < 0 ? 'text-success-600' : ''}`}>
                                    {costVariancePct >= 0 ? '+' : ''}{formatPercent(costVariancePct)}
                                  </span>
                                </div>
                                <div className="flex justify-between border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {costVariance >= 0 ? "Under Budget" : "Over Budget"}
                                  </span>
                                  <SensitiveData>
                                    <span className={`font-medium ${costVariance >= 0 ? "text-success-600" : "text-error-600"}`}>
                                      {formatCurrency(Math.abs(costVariance))}
                                    </span>
                                  </SensitiveData>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {/* Hour Utilisation card */}
                      {(() => {
                        const budgetHours = enrichedBudgetData?.summary?.hours?.budget ?? project.budgetHours ?? 0;
                        const actualHours = enrichedBudgetData?.summary?.hours?.actual ?? project.actualHours ?? 0;
                        const { formulaType } = getUtilisationSettings();
                        const utilisationPct = computeUtilisationRate({ ...project, budgetHours, actualHours }, formulaType);
                        const utilisationColor = utilisationPct > 100 ? 'text-error-600' : utilisationPct > 85 ? 'text-warning-600' : 'text-success-600';
                        const trendIcon = utilisationPct > 100
                          ? <TrendingUp className="h-4 w-4 text-error-500" />
                          : utilisationPct > 85
                            ? <TrendingUp className="h-4 w-4 text-warning-500" />
                            : <TrendingDown className="h-4 w-4 text-success-500" />;
                        const hoursVariance = budgetHours - actualHours;
                        const hoursVariancePct = budgetHours > 0 ? ((actualHours - budgetHours) / budgetHours) * 100 : 0;
                        // Completion context: same as autoCalculatedCompletionPercent — hours as a proxy for progress
                        const impliedCompletion = Math.round(utilisationPct);
                        const completionPctForHours = effectiveCompletionPercent;
                        const impliedGap = impliedCompletion - completionPctForHours;
                        return (
                          <Card>
                            <CardHeader className="mt-2 px-4 pb-1">
                              <CardTitle className="text-sm font-medium">Hour Utilisation</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pt-2 pb-3">
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className={`text-2xl font-bold ${utilisationColor}`}>
                                  {utilisationPct.toFixed(1)}%
                                </div>
                                {trendIcon}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">Of budget hours used</p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => setActiveTab('resource-budget')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >
                                    Budget Hours
                                  </button>
                                  <span className="font-medium">{budgetHours.toLocaleString()} hrs</span>
                                </div>
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => setActiveTab('time')}
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap"
                                  >
                                    Actual Hours
                                  </button>
                                  <span className="font-medium">{actualHours.toLocaleString()} hrs</span>
                                </div>
                                <div className="flex justify-between border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">
                                    {hoursVariance >= 0 ? 'Remaining' : 'Over Budget'}
                                  </span>
                                  <span className={`font-medium ${hoursVariance >= 0 ? 'text-success-600' : 'text-error-600'}`}>
                                    {Math.abs(hoursVariance).toLocaleString()} hrs
                                    {hoursVariancePct !== 0 && (
                                      <span className="ml-1 text-muted-foreground font-normal">
                                        ({hoursVariancePct >= 0 ? '+' : ''}{hoursVariancePct.toFixed(1)}%)
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {/* Implied completion — cross-references Completion card */}
                                <div className="flex justify-between items-center border-t pt-1.5 mt-1">
                                  <span className="text-muted-foreground">Implied completion</span>
                                  <span className={`font-medium ${impliedGap > 10 ? 'text-error-600' : impliedGap > 5 ? 'text-warning-600' : 'text-success-600'}`}>
                                    {impliedCompletion}%
                                    {Math.abs(impliedGap) > 5 && (
                                      <span className="ml-1 text-muted-foreground font-normal">
                                        ({impliedGap > 0 ? '+' : ''}{impliedGap}% vs actual)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {(() => {
                        const completionPct = effectiveCompletionPercent;
                        const timeElapsed = project.startDate && project.endDate
                          ? Math.min(100, Math.max(0, Math.round(
                              (new Date().getTime() - new Date(project.startDate).getTime()) /
                              (new Date(project.endDate).getTime() - new Date(project.startDate).getTime()) * 100
                            )))
                          : null;
                        const timeGap = timeElapsed !== null ? timeElapsed - completionPct : 0;
                        const hoursGap = autoCalculatedCompletionPercent - completionPct;
                        const isAtRisk = timeGap > 20 || hoursGap > 20;
                        const isWarning = !isAtRisk && (timeGap > 10 || hoursGap > 10);
                        const heroColor = isAtRisk ? 'text-error-600' : isWarning ? 'text-warning-600' : 'text-success-600';
                        const timeColor = timeGap > 20 ? 'text-error-600' : timeGap > 10 ? 'text-warning-600' : 'text-success-600';
                        const hoursColor = hoursGap > 20 ? 'text-error-600' : hoursGap > 10 ? 'text-warning-600' : 'text-success-600';
                        const isFixedPctComplete = project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete';
                        const isManualOverride = isFixedPctComplete && project.completionPercentOverride != null;
                        return (
                          <Card>
                            <CardHeader className="mt-2 px-4 pb-1">
                              <CardTitle className="text-sm font-medium">Completion</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pt-2 pb-3">
                              {/* Hero */}
                              <div className="flex items-center gap-2">
                                <div className={`text-2xl font-bold ${heroColor}`}>
                                  {formatPercent(effectiveCompletionPercent, 0)}
                                </div>
                                <Activity className={`h-4 w-4 ${heroColor}`} />
                                {/* Inline edit pencil for fixed-price % complete */}
                                {isFixedPctComplete && !completionPercentEditing && canEditOverview && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={handleCompletionPercentEditStart}
                                          className="ml-1 p-1 rounded hover:bg-muted opacity-40 hover:opacity-100 transition-opacity"
                                          aria-label="Edit completion percentage"
                                        >
                                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>Override completion %</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>

                              {/* Inline edit input */}
                              {isFixedPctComplete && completionPercentEditing && (
                                <div className="flex items-center gap-2 mt-1">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={completionPercentInputValue}
                                    onChange={(e) => setCompletionPercentInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleCompletionPercentEditConfirm();
                                      if (e.key === 'Escape') handleCompletionPercentEditCancel();
                                    }}
                                    className="w-20 h-8 text-lg font-bold"
                                    autoFocus
                                  />
                                  <span className="text-lg font-bold">%</span>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditConfirm}>Save</Button>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleCompletionPercentEditCancel}>Cancel</Button>
                                </div>
                              )}

                              <p className="text-xs text-muted-foreground mt-0.5">
                                {isManualOverride ? 'Manual override' : isFixedPctComplete ? 'Auto · based on approved hours' : 'Based on hours logged'}
                              </p>

                              {/* Secondary rows */}
                              <div className="mt-3 space-y-1.5 text-xs">
                                {/* Hours signal — same figure as Hours Utilisation card's "Implied completion" */}
                                <div className="flex justify-between items-baseline">
                                  <button
                                    className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity whitespace-nowrap shrink-0"
                                    onClick={() => setActiveTab('resource-budget')}
                                  >
                                    Hours utilisation
                                  </button>
                                  <span className={`font-medium text-right ${hoursColor}`}>
                                    {autoCalculatedCompletionPercent}%
                                    {hoursGap > 5 && <span className="text-muted-foreground font-normal ml-1 whitespace-nowrap">({hoursGap}% behind)</span>}
                                    {hoursGap < -5 && <span className="text-muted-foreground font-normal ml-1 whitespace-nowrap">({Math.abs(hoursGap)}% ahead)</span>}
                                  </span>
                                </div>

                                {/* Timeline signal: how much of the project timeline has elapsed */}
                                {timeElapsed !== null && (
                                  <div className="flex justify-between items-baseline">
                                    <span className="text-muted-foreground whitespace-nowrap shrink-0">Timeline elapsed</span>
                                    <span className={`font-medium text-right ${timeColor}`}>
                                      {timeElapsed}%
                                      {timeGap > 5 && <span className="text-muted-foreground font-normal ml-1 whitespace-nowrap">({timeGap}% gap)</span>}
                                      {timeGap < -5 && <span className="text-muted-foreground font-normal ml-1 whitespace-nowrap">({Math.abs(timeGap)}% ahead)</span>}
                                    </span>
                                  </div>
                                )}

                                {/* Separator + overall health */}
                                <div className="border-t pt-1.5 space-y-1.5">
                                  <div className="flex justify-between items-baseline">
                                    <span className={`font-medium whitespace-nowrap shrink-0 ${heroColor}`}>
                                      {isAtRisk ? '⚠ At risk' : isWarning ? '△ Needs attention' : '✓ On track'}
                                    </span>
                                    <span className="text-muted-foreground text-right whitespace-nowrap ml-2">
                                      {timeElapsed !== null && timeGap > 5
                                        ? `${timeGap}% behind schedule`
                                        : timeElapsed !== null && timeGap < -5
                                        ? `${Math.abs(timeGap)}% ahead of schedule`
                                        : hoursGap > 5
                                        ? `${hoursGap}% behind on hours`
                                        : hoursGap < -5
                                        ? `${Math.abs(hoursGap)}% ahead on hours`
                                        : 'All signals green'}
                                    </span>
                                  </div>

                                  {/* Manual override suggestion */}
                                  {isFixedPctComplete && isManualOverride && autoCalculatedCompletionPercent !== effectiveCompletionPercent && (
                                    <div className="flex justify-between items-center text-xs bg-muted/50 rounded px-1.5 py-1">
                                      <span className="text-muted-foreground">Hours suggest {autoCalculatedCompletionPercent}%</span>
                                      <button
                                        className="text-primary underline underline-offset-2 hover:no-underline font-medium"
                                        onClick={() => handleCompletionModeChange('based-on-time')}
                                      >
                                        Use this instead
                                      </button>
                                    </div>
                                  )}

                                  {/* Audit History link — only relevant for fixed-price % complete manual overrides */}
                                  {project.type === 'fixed-price' && project.billingTriggerType === 'percent-complete' && completionAuditEntries.length > 0 && (
                                    <div className="flex justify-between items-center text-xs">
                                      <button
                                        className="text-primary underline underline-offset-2 hover:opacity-70 transition-opacity font-medium whitespace-nowrap"
                                        onClick={() => {
                                          setAuditCategoryFilter('completion');
                                          setActiveTab('audit');
                                        }}
                                      >
                                        View Audit History
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* ── TWO-COLUMN LAYOUT ── */}
                <div className="flex flex-col gap-4">
                <div className="grid gap-4 md:grid-cols-2 items-start">

                  {/* ══ LEFT COLUMN: Merged Info card, Key People ══ */}
                  <div className="flex flex-col gap-4">

                  {/* ── Merged Project Information + Client Information card ── */}
                  <Card className={isProjectInfoEditMode || isClientInfoEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                    {/* ── Unified card header with single edit button ── */}
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>Project Information</CardTitle>
                        {!isProjectInfoEditMode && !isClientInfoEditMode && canEditOverview && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={handleEnterUnifiedEditMode}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  aria-label="Edit project and client information"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="left">Edit project and client information</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {isProjectInfoEditMode ? (
                        <>
                          {/* Project Type */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="edit-type">Project Type</Label>
                              {isTypeLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      There are outstanding sent invoices. Project type cannot be changed until all invoices are paid or removed.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {isTypeLocked ? (
                              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                                {formatProjectType(project.type)}
                              </div>
                            ) : (
                              <Select
                                value={editFormData.type || project.type}
                                onValueChange={(value) => handleFieldChange("type", value)}
                              >
                                <SelectTrigger id="edit-type">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fixed-price">Fixed Price</SelectItem>
                                  <SelectItem value="time-and-materials">Time & Materials</SelectItem>
                                  <SelectItem value="retainer">Retainer</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {/* Tier-specific warnings */}
                            {!isTypeLocked && wipInvoiceTier === 'draft-exist' && (
                              <p className="flex items-start gap-1.5 text-xs text-error-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Draft WIP invoices must be removed before changing project type — they&apos;ve already captured the current billing type.
                              </p>
                            )}
                            {!isTypeLocked && wipInvoiceTier === 'all-paid' && (editFormData.type !== undefined && editFormData.type !== project.type) && (
                              <p className="flex items-start gap-1.5 text-xs text-warning-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                All existing invoices are paid. You can proceed but this change affects future billing.
                              </p>
                            )}
                            {!isTypeLocked && wipInvoiceTier === 'none' && (editFormData.type !== undefined && editFormData.type !== project.type) && (
                              <p className="flex items-start gap-1.5 text-xs text-warning-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Changing project type will affect billing setup. Please review billing configuration after saving.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* Start Date */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-start-date">Start Date</Label>
                            <DateInput
                              id="edit-start-date"
                              value={formatDateForInput(editFormData.startDate !== undefined ? editFormData.startDate : project.startDate)}
                              onChange={(v) => handleFieldChange("startDate", v)}
                            />
                            {startDateMovedPastTimesheets && (
                              <p className="flex items-start gap-1.5 text-xs text-warning-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                Moving the start date forward may orphan time entries submitted before this date.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* End Date */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-end-date">End Date</Label>
                            <DateInput
                              id="edit-end-date"
                              value={formatDateForInput(editFormData.endDate !== undefined ? editFormData.endDate : project.endDate)}
                              onChange={(v) => handleFieldChange("endDate", v)}
                              className={endDateBeforeStart ? "border-error-500 focus-visible:ring-error-500" : ""}
                            />
                            {endDateBeforeStart && (
                              <p className="flex items-start gap-1.5 text-xs text-error-700">
                                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                End date cannot be before the start date.
                              </p>
                            )}
                            {!endDateBeforeStart && endDateShortenedPastAllocations && (
                              <p className="flex items-start gap-1.5 text-xs text-warning-700">
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                This end date is before some existing resource bookings. Review the Resourcing tab after saving.
                              </p>
                            )}
                          </div>
                          <Separator />

                          {/* Forecast End Date */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-forecast-end-date">Forecast End Date</Label>
                            <DateInput
                              id="edit-forecast-end-date"
                              value={formatDateForInput(
                                editFormData.forecastEndDate !== undefined
                                  ? editFormData.forecastEndDate
                                  : project.forecastEndDate ?? ""
                              )}
                              onChange={(v) =>
                                handleFieldChange("forecastEndDate", v || undefined)
                              }
                            />
                            {(() => {
                              const forecastVal = editFormData.forecastEndDate !== undefined
                                ? editFormData.forecastEndDate
                                : project.forecastEndDate;
                              const plannedEnd = editFormData.endDate !== undefined ? editFormData.endDate : project.endDate;
                              if (!forecastVal || !plannedEnd) return null;
                              const delayDays = Math.ceil(
                                (new Date(forecastVal).getTime() - new Date(plannedEnd).getTime()) / 86_400_000
                              );
                              if (delayDays > 0) {
                                return (
                                  <p className="flex items-center gap-1.5 text-xs text-warning-700">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                    {delayDays} day{delayDays !== 1 ? "s" : ""} after planned end
                                  </p>
                                );
                              }
                              return (
                                <p className="flex items-center gap-1.5 text-xs text-success-700">
                                  <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                                  On track
                                </p>
                              );
                            })()}
                            <p className="text-xs text-muted-foreground">
                              Leave blank if no forecast has been set.
                            </p>
                          </div>
                          <Separator />

                          {/* Project Manager */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-project-manager">Project Manager</Label>
                            <EmployeeCombobox
                              employees={assignableEmployees}
                              value={editFormData.projectManagerId !== undefined
                                ? String(editFormData.projectManagerId)
                                : projectManager?.id !== undefined ? String(projectManager.id) : undefined}
                              onValueChange={handleProjectManagerChange}
                              placeholder="Select a project manager..."
                              recentEmployeeIds={recentProjectManagerIds}
                              maxRecentEmployees={5}
                            />
                          </div>
                          <Separator />

                          {/* Billable */}
                          <div className="flex items-center justify-between">
                            <div>
                              <Label htmlFor="edit-billable" className="text-sm font-medium">Billable</Label>
                              <p className="text-xs text-muted-foreground mt-0.5">Mark this project as billable to the client</p>
                            </div>
                            <Switch
                              id="edit-billable"
                              checked={editFormData.billable !== undefined ? Boolean(editFormData.billable) : (project.billable ?? true)}
                              onCheckedChange={handleBillableToggle}
                            />
                          </div>
                          <Separator />

                          {/* Revenue Recognition */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="edit-rev-rec">Revenue Recognition</Label>
                              {isRevRecLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Revenue recognition cannot be changed once revenue has been recognised. Contact your Finance team.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {isRevRecLocked ? (
                              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                                {formatRevenueRecognition(project.revenueRecognitionMethod)}
                              </div>
                            ) : (
                              <Select
                                value={editFormData.revenueRecognitionMethod || project.revenueRecognitionMethod}
                                onValueChange={(value) => handleFieldChange("revenueRecognitionMethod", value)}
                              >
                                <SelectTrigger id="edit-rev-rec">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="percentage-of-completion">Percentage of Completion</SelectItem>
                                  <SelectItem value="completed-contract">Completed Contract</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>

                        </>
                      ) : (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Type</span>
                            <span className="font-medium">{formatProjectType(project.type)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Start Date</span>
                            <span className="font-medium">{formatDate(project.startDate)}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-start">
                            <span className="text-muted-foreground">End Date</span>
                            <div className="text-right">
                              <span className="font-medium">{formatDate(project.endDate)}</span>
                              {project.endDate && project.statusId !== 6 && (() => {
                                const daysRemaining = Math.ceil(
                                  (new Date(project.endDate).getTime() - new Date().setHours(0,0,0,0)) / 86_400_000
                                );
                                if (daysRemaining < 0) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-error-600 mt-0.5">
                                      <AlertCircle className="h-3 w-3 shrink-0" />
                                      {Math.abs(daysRemaining)} days overdue
                                    </p>
                                  );
                                } else if (daysRemaining < 15) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-error-600 mt-0.5">
                                      <AlertCircle className="h-3 w-3 shrink-0" />
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                } else if (daysRemaining <= 30) {
                                  return (
                                    <p className="flex items-center justify-end gap-1 text-xs text-warning-600 mt-0.5">
                                      <Clock className="h-3 w-3 shrink-0" />
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                } else {
                                  return (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {daysRemaining} days remaining
                                    </p>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                          <Separator />

                          {/* Forecast End Date — view mode */}
                          <div className="flex justify-between items-start">
                            <span className="text-muted-foreground">Forecast End Date</span>
                            <div className="text-right">
                              {project.forecastEndDate ? (
                                <>
                                  <span className="font-medium">{formatDate(project.forecastEndDate)}</span>
                                  {(() => {
                                    if (!project.endDate) return null;
                                    const delayDays = Math.ceil(
                                      (new Date(project.forecastEndDate).getTime() - new Date(project.endDate).getTime()) / 86_400_000
                                    );
                                    if (delayDays > 0) {
                                      return (
                                        <p className="flex items-center justify-end gap-1 text-xs text-warning-600 mt-0.5">
                                          <AlertTriangle className="h-3 w-3 shrink-0" />
                                          {delayDays} day{delayDays !== 1 ? "s" : ""} after planned end
                                        </p>
                                      );
                                    }
                                    return (
                                      <p className="flex items-center justify-end gap-1 text-xs text-success-600 mt-0.5">
                                        <CheckCircle className="h-3 w-3 shrink-0" />
                                        On track
                                      </p>
                                    );
                                  })()}
                                </>
                              ) : (
                                <span className="text-muted-foreground italic">Not set</span>
                              )}
                            </div>
                          </div>
                          <Separator />

                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Project Manager</span>
                            <span className="font-medium">{projectManager?.name || "Unknown"}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1.5">
                              <span className="text-muted-foreground">Revenue Recognition</span>
                              {isRevRecLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Revenue recognition cannot be changed once revenue has been recognised. Contact your Finance team.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <span className="font-medium">{formatRevenueRecognition(project.revenueRecognitionMethod)}</span>
                          </div>
                          <Separator />
                          {/* Billable — view mode */}
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Billable</span>
                            {(project.billable ?? true) ? (
                              <Badge variant="outline" className="text-xs font-medium text-success-700 bg-success-50 border-success-200">
                                Billable
                              </Badge>
                            ) : (
                              <span className="inline-flex items-center rounded-full border border-muted bg-muted/40 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                                Non-billable
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>

                    {/* ── Dividing line between the two sections ── */}
                    <Separator />

                    {/* ── Client Information section ── */}
                    <CardHeader>
                      <CardTitle>Client Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* On Stop / Suspended warning */}
                      {client && (client.accountStatus === 'on-stop' || client.accountStatus === 'suspended') && (
                        <div className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 px-3 py-2.5 text-sm text-warning-800">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />
                          <span>
                            <span className="font-semibold">This account is ON STOP in the Finance System.</span>{' '}
                            Contact Finance before proceeding with invoicing or scope changes.
                          </span>
                        </div>
                      )}

                      {isClientInfoEditMode ? (
                        <>
                          {/* Client — editable in edit mode */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="client-name-edit">Client</Label>
                              {isClientChangeLocked && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="text-muted-foreground cursor-help">
                                        <Lock className="h-3.5 w-3.5" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[260px] text-xs">
                                      Client cannot be changed — this project has financial transactions.
                                      Contact your Finance team to process a client transfer in the Finance System.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <ClientCombobox
                              clients={clients}
                              value={client?.id !== undefined ? String(client.id) : undefined}
                              onValueChange={handleClientChange}
                              placeholder="Select a client..."
                              recentClientIds={recentClientIds}
                              maxRecentClients={5}
                              disabled={isClientChangeLocked}
                            />
                          </div>

                          <Separator />

                          {/* Account Manager — editable in edit mode */}
                          <div className="space-y-1.5">
                            <Label htmlFor="edit-account-manager">Account Manager</Label>
                            <EmployeeCombobox
                              employees={assignableEmployees}
                              value={
                                clientInfoEditData.accountManagerId !== undefined
                                  ? String(clientInfoEditData.accountManagerId)
                                  : accountManager?.id !== undefined
                                    ? String(accountManager.id)
                                    : undefined
                              }
                              onValueChange={handleAccountManagerChange}
                              placeholder="Select an account manager..."
                              recentEmployeeIds={recentAccountManagerIds}
                              maxRecentEmployees={5}
                            />
                          </div>

                          <Separator />

                          {/* Read-only fields in edit mode */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Contact</span>
                            <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            {client?.contactEmail ? (
                              <a
                                href={`mailto:${client.contactEmail}`}
                                className="font-medium text-sm text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.contactEmail}
                              </a>
                            ) : (
                              <span className="font-medium text-sm">Unknown</span>
                            )}
                          </div>

                        </>
                      ) : (
                        <>
                          {/* Client Name — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Client</span>
                            {client ? (
                              <button
                                type="button"
                                onClick={() => openClientPanel(client.id)}
                                className="font-medium text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.code ? `${client.code} - ${client.name}` : client.name}
                              </button>
                            ) : (
                              <span className="font-medium">Unknown</span>
                            )}
                          </div>
                          <Separator />

                          {/* Account Manager — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Account Manager</span>
                            <span className="font-medium">
                              {accountManager?.name || <span className="text-muted-foreground italic text-sm">Not assigned</span>}
                            </span>
                          </div>
                          <Separator />

                          {/* Contact — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Contact</span>
                            <span className="font-medium">{client?.contactName || 'Unknown'}</span>
                          </div>
                          <Separator />

                          {/* Email — read-only view */}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Email</span>
                            {client?.contactEmail ? (
                              <a
                                href={`mailto:${client.contactEmail}`}
                                className="font-medium text-sm text-primary hover:underline underline-offset-2 transition-colors"
                              >
                                {client.contactEmail}
                              </a>
                            ) : (
                              <span className="font-medium text-sm">Unknown</span>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>

                    {/* ── Unified Save / Cancel at bottom of merged card ── */}
                    {(isProjectInfoEditMode || isClientInfoEditMode) && (
                      <CardContent className="pt-0">
                        <div className="flex justify-end gap-2 pt-2">
                          <Button variant="outline" size="sm" onClick={handleCancelUnifiedEdit} disabled={isSavingUnified}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveUnifiedChanges}
                            disabled={
                              isSavingUnified ||
                              !hasUnifiedUnsavedChanges ||
                              isSaveBlocked ||
                              (wipInvoiceTier === 'draft-exist' && editFormData.type !== undefined && editFormData.type !== project.type)
                            }
                          >
                            {isSavingUnified && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                            {isSavingUnified ? "Saving…" : "Save Changes"}
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>{/* end merged Project + Client card */}

                  {/* Key People card — left column */}
                  <Card className={isKeyPeopleEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Key People</CardTitle>
                          {!isKeyPeopleEditMode && canPerformAction('assignKeyPeople') && isTabEditable('overview') && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterKeyPeopleEditMode}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit key people"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">Edit key people</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isKeyPeopleEditMode ? (
                          /* ── Edit Mode ── */
                          <div className="space-y-4">
                            {PROJECT_ROLE_DISPLAY_ORDER.filter(isRoleEnabled).map((role, idx) => {
                              const required = isRoleRequired(role);
                              const label = getRoleLabel(role);
                              const currentValue = keyPeopleEditState[role] ?? '';
                              return (
                                <div key={role} className="space-y-1.5">
                                  {idx > 0 && <Separator />}
                                  <Label>
                                    {label}
                                    {required && <span className="text-error-500 ml-0.5">*</span>}
                                  </Label>
                                  <EmployeeCombobox
                                    employees={assignableEmployees}
                                    value={currentValue || undefined}
                                    onValueChange={(val) => handleKeyPeopleRoleChange(role, val)}
                                    placeholder={`Select ${label}...`}
                                    recentEmployeeIds={[]}
                                    maxRecentEmployees={5}
                                  />
                                  {required && !currentValue && hasKeyPeopleUnsavedChanges && (
                                    <p className="text-xs text-error-600 flex items-center gap-1">
                                      <AlertCircle className="h-3 w-3" />
                                      {label} is required
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={handleCancelKeyPeopleEdit} disabled={isSavingKeyPeople}>
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleSaveKeyPeople}
                                disabled={isSavingKeyPeople || !hasKeyPeopleUnsavedChanges || !keyPeopleEditState['project-manager']}
                              >
                                {isSavingKeyPeople && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                {isSavingKeyPeople ? "Saving…" : "Save Changes"}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read Mode ── */
                          (() => {
                            // C6: derive assignments — prefer roleAssignments, fall back to projectManagerId
                            const assignments: Array<{ role: ProjectRoleType; name: string; jobTitle?: string }> = [];
                            if (project.roleAssignments && project.roleAssignments.length > 0) {
                              const assignmentMap = new Map<string, ProjectRoleAssignment>(
                                (project.roleAssignments as ProjectRoleAssignment[]).map((a) => [a.role, a])
                              );
                              PROJECT_ROLE_DISPLAY_ORDER.forEach((role) => {
                                const a = assignmentMap.get(role);
                                if (a) assignments.push({ role, name: a.employeeName, jobTitle: a.employeeJobTitle });
                              });
                            } else if (project.projectManagerId && projectManager) {
                              assignments.push({
                                role: 'project-manager',
                                name: projectManager.name,
                                jobTitle: projectManager.role ?? undefined,
                              });
                            }

                            if (assignments.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
                                  <div className="rounded-full bg-muted p-3">
                                    <Users className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                  <p className="text-sm font-medium">No key people assigned</p>
                                  <p className="text-xs text-muted-foreground">
                                    Add role assignments to track accountability for this project.
                                  </p>
                                  {canPerformAction('assignKeyPeople') && isTabEditable('overview') && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="mt-1"
                                      onClick={handleEnterKeyPeopleEditMode}
                                    >
                                      <Pencil className="h-3 w-3 mr-1.5" />
                                      Add people
                                    </Button>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-3">
                                {assignments.map(({ role, name, jobTitle }, idx) => {
                                  const initials = name
                                    .split(' ')
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((w) => w[0].toUpperCase())
                                    .join('');
                                  return (
                                    <div key={role}>
                                      {idx > 0 && <Separator className="mb-3" />}
                                      <div className="flex items-center gap-3">
                                        <Avatar className="h-8 w-8 shrink-0">
                                          <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                            {initials}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium truncate">{name}</span>
                                            <span className="text-xs text-muted-foreground shrink-0">
                                              {getRoleLabel(role)}
                                            </span>
                                          </div>
                                          {jobTitle && (
                                            <p className="text-xs text-muted-foreground truncate">{jobTitle}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        )}
                      </CardContent>
                    </Card>


                  </div>{/* end LEFT COLUMN */}

                  {/* ══ RIGHT COLUMN: Project Attributes, Project Objectives, Key Deliverables ══ */}
                  <div className="flex flex-col gap-4">

                    {/* Project Attributes */}
                    <Card className={isAttributesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Project Attributes</CardTitle>
                          {!isAttributesEditMode && canEditOverview && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterAttributesEditMode}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit project attributes"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left">Edit project attributes</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {isAttributesEditMode ? (
                          customFieldDefs.length === 0 ? (
                            <div className="space-y-4">
                              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground gap-1.5">
                                <SlidersHorizontal className="h-6 w-6 opacity-30" />
                                <p className="text-sm">No project attributes have been configured</p>
                                <p className="text-xs text-center">Attributes are defined in Settings before they can be set on a project.</p>
                              </div>
                              <div className="flex justify-end gap-2 pt-2">
                                <Button variant="outline" size="sm" onClick={handleCancelAttributesEdit}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                          <div className="space-y-4">
                            {customFieldDefs
                              .sort((a: ProjectCustomFieldDefinition, b: ProjectCustomFieldDefinition) => a.displayOrder - b.displayOrder)
                              .map((def: ProjectCustomFieldDefinition, idx: number) => {
                                const value = attributesEditState[def.id] ?? '';
                                const fieldError = attributesValidationErrors[def.id];
                                return (
                                  <div key={def.id} className="space-y-1.5">
                                    {idx > 0 && <Separator />}
                                    <Label className="text-xs text-muted-foreground">
                                      {def.label}
                                      {def.required && <span className="text-error-500 ml-0.5">*</span>}
                                    </Label>
                                    {def.fieldType === 'text' && (
                                      <Input
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev: Record<string, string>) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        placeholder={def.placeholder ?? ''}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}
                                    {def.fieldType === 'number' && (
                                      <Input
                                        type="number"
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev: Record<string, string>) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        placeholder={def.placeholder ?? ''}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}
                                    {def.fieldType === 'currency' && (
                                      <div className="relative">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                                        <Input
                                          type="number"
                                          value={value}
                                          onChange={(e) => {
                                            setAttributesEditState((prev: Record<string, string>) => ({ ...prev, [def.id]: e.target.value }));
                                            if (fieldError) setAttributesValidationErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next[def.id]; return next; });
                                          }}
                                          placeholder={def.placeholder ?? '0.00'}
                                          className={cn('h-8 text-sm pl-6', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                        />
                                      </div>
                                    )}
                                    {def.fieldType === 'date' && (
                                      <Input
                                        type="date"
                                        value={value}
                                        onChange={(e) => {
                                          setAttributesEditState((prev: Record<string, string>) => ({ ...prev, [def.id]: e.target.value }));
                                          if (fieldError) setAttributesValidationErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                        className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}
                                      />
                                    )}
                                    {def.fieldType === 'boolean' && (
                                      <div className="flex items-center gap-2 pt-0.5">
                                        <Switch
                                          checked={value === 'true'}
                                          onCheckedChange={(checked) =>
                                            setAttributesEditState((prev: Record<string, string>) => ({
                                              ...prev,
                                              [def.id]: checked ? 'true' : 'false',
                                            }))
                                          }
                                        />
                                        <span className="text-sm">{value === 'true' ? 'Yes' : 'No'}</span>
                                      </div>
                                    )}
                                    {def.fieldType === 'select' && (
                                      <Select
                                        value={value || '__none__'}
                                        onValueChange={(v) => {
                                          setAttributesEditState((prev: Record<string, string>) => ({
                                            ...prev,
                                            [def.id]: v === '__none__' ? '' : v,
                                          }));
                                          if (fieldError) setAttributesValidationErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next[def.id]; return next; });
                                        }}
                                      >
                                        <SelectTrigger className={cn('h-8 text-sm', fieldError && 'border-error-500 focus-visible:ring-error-500')}>
                                          <SelectValue placeholder={def.placeholder ?? 'Select…'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__none__" className="text-sm text-muted-foreground">— None —</SelectItem>
                                          {(def.options ?? []).map((opt: string) => (
                                            <SelectItem key={opt} value={opt} className="text-sm">{opt}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    {fieldError && <p className="text-xs text-error-500">{fieldError}</p>}
                                  </div>
                                );
                              })}
                            <div className="flex justify-end gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={handleCancelAttributesEdit} disabled={isSavingAttributes}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveAttributes} disabled={isSavingAttributes}>
                                {isSavingAttributes && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                {isSavingAttributes ? "Saving…" : "Save Changes"}
                              </Button>
                            </div>
                          </div>
                          )
                        ) : (
                          (() => {
                            const populated = customFieldDefs
                              .sort((a: ProjectCustomFieldDefinition, b: ProjectCustomFieldDefinition) => a.displayOrder - b.displayOrder)
                              .filter((def: ProjectCustomFieldDefinition) => {
                                const v = project.projectCustomFields?.[def.id];
                                return v !== undefined && v !== '';
                              });
                            if (populated.length === 0) {
                              return (
                                <div className="flex flex-col items-center justify-center py-6 text-muted-foreground gap-1.5">
                                  <SlidersHorizontal className="h-6 w-6 opacity-30" />
                                  <p className="text-sm">No attributes set</p>
                                  {canEditOverview && (
                                    <button type="button" onClick={handleEnterAttributesEditMode} className="text-xs text-primary hover:underline underline-offset-2">
                                      Add project attributes
                                    </button>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-2">
                                {populated.map((def: ProjectCustomFieldDefinition, idx: number) => {
                                  const rawValue = project.projectCustomFields![def.id];
                                  let displayValue: ReactNode = rawValue;
                                  if (def.fieldType === 'currency') {
                                    const num = parseFloat(rawValue);
                                    displayValue = isNaN(num) ? rawValue : `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                  } else if (def.fieldType === 'number') {
                                    const num = parseFloat(rawValue);
                                    displayValue = isNaN(num) ? rawValue : num.toLocaleString('en-GB');
                                  } else if (def.fieldType === 'date') {
                                    const d = new Date(rawValue);
                                    displayValue = isNaN(d.getTime()) ? rawValue : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                                  } else if (def.fieldType === 'boolean') {
                                    displayValue = rawValue === 'true'
                                      ? <Badge variant="outline" className="text-xs font-normal bg-success-50 text-success-700 border-success-200">Yes</Badge>
                                      : <Badge variant="outline" className="text-xs font-normal text-muted-foreground">No</Badge>;
                                  }
                                  return (
                                    <div key={def.id}>
                                      {idx > 0 && <Separator className="my-2" />}
                                      <div className="flex justify-between items-center gap-2">
                                        <span className="text-muted-foreground text-sm">{def.label}</span>
                                        <span className="font-medium text-sm text-right">{displayValue}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        )}
                      </CardContent>
                    </Card>

                    {/* Project Objectives */}
                    {contentFieldSettings.showObjectives && (
                    <Card className={isObjectivesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Project Objectives</CardTitle>
                          {!isObjectivesEditMode && canEditOverview && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterObjectivesEdit}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit objectives"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit objectives</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isObjectivesEditMode ? (
                          <div className="space-y-3">
                            <Textarea
                              value={objectivesEditValue}
                              onChange={(e) => setObjectivesEditValue(e.target.value)}
                              placeholder="Describe why this project exists — strategic goals, success criteria, what 'done well' looks like for this engagement."
                              className="min-h-[150px] resize-y"
                              rows={6}
                              maxLength={2000}
                            />
                            <p className="text-xs text-muted-foreground text-right">{objectivesEditValue.length}/2,000 (advisory)</p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={handleCancelObjectivesEdit} disabled={isSavingObjectives}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveObjectives} disabled={isSavingObjectives}>
                                {isSavingObjectives && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                {isSavingObjectives ? "Saving…" : "Save"}
                              </Button>
                            </div>
                          </div>
                        ) : project?.objectives ? (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.objectives}</p>
                        ) : canEditOverview ? (
                          <button type="button" onClick={handleEnterObjectivesEdit} className="w-full text-left group">
                            <p className="text-sm text-muted-foreground italic group-hover:text-foreground transition-colors">
                              No objectives defined yet — add what this project is trying to achieve.
                            </p>
                          </button>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No objectives defined.</p>
                        )}
                      </CardContent>
                    </Card>
                    )}

                    {/* Key Deliverables */}
                    {contentFieldSettings.showKeyDeliverables && (
                    <Card className={isDeliverablesEditMode ? "ring-2 ring-info-200 bg-info-50/30" : ""}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle>Key Deliverables</CardTitle>
                          {!isDeliverablesEditMode && canEditOverview && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={handleEnterDeliverablesEdit}
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    aria-label="Edit key deliverables"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit key deliverables</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isDeliverablesEditMode ? (
                          <div className="space-y-3">
                            <Textarea
                              value={deliverablesEditValue}
                              onChange={(e) => setDeliverablesEditValue(e.target.value)}
                              placeholder="Describe what this project will produce — tangible outputs, artefacts, systems, or services being delivered."
                              className="min-h-[150px] resize-y"
                              rows={6}
                              maxLength={2000}
                            />
                            <p className="text-xs text-muted-foreground text-right">{deliverablesEditValue.length}/2,000 (advisory)</p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={handleCancelDeliverablesEdit} disabled={isSavingDeliverables}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveDeliverables} disabled={isSavingDeliverables}>
                                {isSavingDeliverables && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                                {isSavingDeliverables ? "Saving…" : "Save"}
                              </Button>
                            </div>
                          </div>
                        ) : project?.keyDeliverables ? (
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{project.keyDeliverables}</p>
                        ) : canEditOverview ? (
                          <button type="button" onClick={handleEnterDeliverablesEdit} className="w-full text-left group">
                            <p className="text-sm text-muted-foreground italic group-hover:text-foreground transition-colors">
                              No deliverables listed yet — describe what this project will produce.
                            </p>
                          </button>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No deliverables listed.</p>
                        )}
                      </CardContent>
                    </Card>
                    )}

                  </div>{/* end RIGHT COLUMN */}
                </div>{/* end two-column grid */}

                {/* ── Project Notes — full width, spans both columns ── */}
                {contentFieldSettings.showNotes && (
                  <Card>
                    <CardHeader className="mt-3 px-4 pb-1">
                      <div className="flex items-center gap-2">
                        <CardTitle>Project Notes</CardTitle>
                        {noteLog.length > 0 && (
                          <Badge variant="secondary" className="text-xs font-normal">
                            {noteLog.length} {noteLog.length === 1 ? "note" : "notes"}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pt-2 pb-4 space-y-4">
                      {/* Compose box — always visible at the top */}
                      <div className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-3">
                        <Textarea
                          value={newNoteValue}
                          onChange={(e) => setNewNoteValue(e.target.value)}
                          placeholder="Add a note — decisions made, client context, escalations, handover information…"
                          className="min-h-[80px] resize-y bg-background border-muted"
                          rows={3}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            Press <kbd className="rounded border border-border bg-muted px-1 py-0.5 text-[10px] font-mono">Ctrl+Enter</kbd> to add
                          </p>
                          <Button
                            size="sm"
                            onClick={handleAddNote}
                            disabled={!newNoteValue.trim() || isSubmittingNote}
                            className="gap-1.5"
                          >
                            {isSubmittingNote ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5" />
                            )}
                            {isSubmittingNote ? "Adding…" : "Add Note"}
                          </Button>
                        </div>
                      </div>

                      {/* Note entries — newest first */}
                      {noteLog.length > 0 ? (
                        <div className="space-y-3">
                          {noteLog.map((note: ProjectNote) => {
                            const date = new Date(note.createdAt);
                            const now = new Date();
                            const diffMs = now.getTime() - date.getTime();
                            const diffMins = Math.floor(diffMs / 60000);
                            const diffHours = Math.floor(diffMs / 3600000);
                            const diffDays = Math.floor(diffMs / 86400000);
                            const relativeTime =
                              diffMins < 1 ? "Just now" :
                              diffMins < 60 ? `${diffMins}m ago` :
                              diffHours < 24 ? `${diffHours}h ago` :
                              diffDays < 7 ? `${diffDays}d ago` :
                              date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                            const fullDateTime = date.toLocaleString("en-GB", {
                              day: "numeric", month: "long", year: "numeric",
                              hour: "2-digit", minute: "2-digit"
                            });
                            return (
                              <div key={note.id} className="flex gap-3 group">
                                {/* Avatar */}
                                <div className="flex-shrink-0 mt-0.5">
                                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-xs font-semibold text-primary">
                                      {note.createdByInitials}
                                    </span>
                                  </div>
                                </div>
                                {/* Note body */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="text-sm font-medium">{note.createdByName}</span>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="text-xs text-muted-foreground cursor-default flex items-center gap-1">
                                            <Clock3 className="h-3 w-3" />
                                            {relativeTime}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          <p>{fullDateTime}</p>
                                          {note.isEdited && note.editedAt && (
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                              Edited {new Date(note.editedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                          )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    {note.isEdited && (
                                      <span className="text-xs text-muted-foreground italic">(edited)</span>
                                    )}
                                  </div>
                                  <p className="text-sm whitespace-pre-wrap leading-relaxed mt-1 text-foreground">
                                    {note.content}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
                          <MessageSquarePlus className="h-8 w-8 opacity-30" />
                          <p className="text-sm font-medium">No notes yet</p>
                          <p className="text-xs">Add the first note above — it will be attributed to you with a timestamp.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                </div>{/* end TWO-COLUMN LAYOUT */}



                {/* Resource Scheduling Widget — hidden for Release 1 */}
                {(false as boolean) && schedulerConnected && liveSummary && (
                  <SchedulingOverviewWidget
                    summary={liveSummary}
                    onViewDetails={() => setActiveTab("scheduling")}
                  />
                )}

      {/* Billable → cascade to cost centres dialog */}
      <AlertDialog open={billableCascadeOpen} onOpenChange={(open) => { if (!open) handleBillableCascadeCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark project as non-billable?</AlertDialogTitle>
            <AlertDialogDescription>
              This project has cost centres that may currently be marked as billable.
              Would you also like to mark them all as non-billable?
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Contextual warning — shown only when the project has existing financial activity */}
          {hasExistingActivity && (
            <div className="rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 flex gap-2.5">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning-500" />
              <div className="space-y-1">
                <p className="font-medium">This project has existing financial activity</p>
                <ul className="list-disc list-inside space-y-0.5 text-warning-700">
                  {hasExistingInvoices && (
                    <li>{billingInvoices.length} invoice{billingInvoices.length !== 1 ? 's' : ''} already raised — these will not be affected</li>
                  )}
                  {hasExistingTimeEntries && (
                    <li>{timeEntries.length} time {timeEntries.length !== 1 ? 'entries' : 'entry'} logged — these will not be affected</li>
                  )}
                </ul>
                <p className="text-warning-700">Marking this project as non-billable will only apply to future activity.</p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleBillableCascadeCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground"
              onClick={() => handleBillableCascadeConfirm(false)}
            >
              Project Only
            </AlertDialogAction>
            <AlertDialogAction onClick={() => handleBillableCascadeConfirm(true)}>
              Update Cost Centres
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
